/* eslint-disable */
import { withStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import React from 'react';
import { compose } from 'redux';
import '../../Services/Order/Order.service.i18n';
import isFunction from '../../Support/Is/Function/IsFunction';
import isString from '../../Support/Is/String/IsString.support';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import I18n from '../I18n/I18n.component';
import KeyValuePairsPropTypes from './KeyValuePairs.component.props';
import keyValuePairsStyles from './KeyValuePairs.component.styles';
import { MFSDR } from 'Constants/MouseFlow/SensitiveDataReplacer';

/**
 * Maps each values in the key value pairs to each corresponding label
 * @param {Object} keyValuePairs The key value pairs
 * @returns {Function} The function to map the label to the value
 */
const mapValueToLabel = (keyValuePairs) => (acc, [field, label, render]) => {
  const fieldValue = get(keyValuePairs, field);
  if (fieldValue === undefined) {
    return acc;
  }
  if (isString(fieldValue)) {
    const values = fieldValue.split(`\r`);
    const fieldValues = values.length > 1 ? values : values[0];
    return [...acc, [label, fieldValues, render]];
  }
  return [...acc, [label, fieldValue, render]];
};

/**
 * KeyValuePairsComponent
 * @export
 * @class KeyValuePairsComponent
 * @extends {React.Component<KeyValuePairsComponentComposedProps, KeyValuePairsComponentState>}
 */
export class KeyValuePairsComponent extends React.Component {
  /**
   * The `props` to seed when any specific prop receives an `undefined` value. NOT a `null` value
   * @static
   * @type {Partial<KeyValuePairsComponentProps>}
   * @memberof KeyValuePairsComponent
   */
  static defaultProps = {};

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`KeyValuePairs`}
   * @memberof KeyValuePairsComponent
   */
  static displayName = `KeyValuePairs`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof KeyValuePairsComponent
   */
  static propTypes = KeyValuePairsPropTypes;

  /**
   * Data specific to the `<KeyValuePairsComponent />` instance
   * @type {KeyValuePairsComponentState}
   * @memberof KeyValuePairsComponent
   */
  state = {};

  /**
   * Render the component
   * @param {boolean} gutterBottom The gutter bottom
   * @returns {Function} The function that returns a rendered `<KeyValuePairsComponent />`
   * @memberof KeyValuePairsComponent
   */
  renderDetailValue = (gutterBottom) => (value) => (
    <Typography gutterBottom={gutterBottom} key={value.name || value}>
      {value.name || value}
    </Typography>
  );

  /**
   * Returns the base input styles
   * @returns {Style} The base input styles
   * @memberof KeyValuePairsComponent
   */
  baseInputProps = () => {
    const { classes } = this.props;
    return {
      classes: {
        root: classes.inputRoot,
      },
      disableUnderline: true,
      readOnly: true,
    };
  };

  /**
   * Render the default field component if no custom field renderer is defined
   * @param {KeyValueProp} value The label value props
   * @returns {React.Component} The default field component
   * @memberof KeyValuePairsComponent
   */
  renderDefaultField = ({ label, value }) => {
    const { classes } = this.props;
    const hasMultipleValues = value.length > 1;
    const values = isArray(value) ? value.map(this.renderDetailValue(hasMultipleValues)) : <Typography>{value.name || value}</Typography>;
    return (
      <Grid item key={label} lg md sm xl xs>
        <I18n variant="caption">{label}</I18n>
        <div className={classes.inputRoot} data-mf-replace-inner={MFSDR}>{values}</div>
      </Grid>
    );
  };

  /**
   * Render the component
   * @returns {React.Component} The rendered `<KeyValuePairsComponent />`
   * @memberof KeyValuePairsComponent
   */
  render() {
    const { keyValuePairs, fieldsMapping, GridContainerProps } = this.props;
    const mapping = fieldsMapping.reduce(mapValueToLabel(keyValuePairs), []);
    return (
      <Grid container direction="column" spacing={1} {...GridContainerProps}>
        {mapping.map(([label, value, render]) => {
          return (
            <React.Fragment key={`${label}${value}`}>
              {isFunction(render) ? render({ InputProps: this.baseInputProps(), label, value }) : this.renderDefaultField({ label, value })}
            </React.Fragment>
          );
        })}
      </Grid>
    );
  }
}

/** @type {React.ComponentClass<KeyValuePairsComponentProps>} */
const KeyValuePairs = compose(withStyles(themeCreateInjectableStyles(keyValuePairsStyles), { withTheme: true }))(KeyValuePairsComponent);

export default KeyValuePairs;
